// Shrinks a photo before it is sent anywhere. Shared rather than kept with receipts because Moo
// accepts images too, and a phone camera file is far larger than either needs.

import { randomId } from "./random-id";

async function render(file: File, sizes: { max: number; quality: number }[]) {
  if (file.size > 20_000_000) throw new Error("Choose a photo under 20 MB.");
  // Browser decode also supports iPhone photos when the browser can decode them.
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return sizes.map(({ max, quality }) => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not prepare the photo.");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/jpeg", quality);
    });
  } catch {
    throw new Error(
      "This photo could not be opened. Choose a JPEG, PNG or WebP photo.",
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function imageData(file: File) {
  return (await render(file, [{ max: 2400, quality: 0.9 }]))[0];
}

/** The photo to keep, and a small preview of it for grids, decoded once. */
export async function imageWithThumbnail(file: File) {
  const [image, thumbnail] = await render(file, [{ max: 2400, quality: 0.9 }, { max: 640, quality: 0.72 }]);
  return { image, thumbnail };
}

export type ReceiptAttachment = { id: string; file: File; sameAsPrevious: boolean };

export function appendReceiptFiles(current: ReceiptAttachment[], files: File[]) {
  const room = Math.max(0, 12 - current.length);
  return [
    ...current,
    ...files.slice(0, room).map((file) => ({
      id: randomId(),
      file,
      sameAsPrevious: false,
    })),
  ];
}

export function receiptGroups<T extends { sameAsPrevious: boolean }>(items: T[]) {
  return items.reduce<T[][]>((groups, item, index) => {
    if (index && item.sameAsPrevious) groups.at(-1)!.push(item);
    else groups.push([item]);
    return groups;
  }, []);
}

/**
 * Each receipt's photos, shrunk for sending, one receipt at a time: every photo is decoded at full
 * size first, and a phone holding several receipts' worth at once can run out of memory. The
 * thumbnails are the grid previews, in the same order.
 */
export async function receiptImageGroups(items: ReceiptAttachment[]) {
  const imageGroups: string[][] = [], thumbnailGroups: string[][] = [];
  for (const group of receiptGroups(items)) {
    const done = await Promise.all(group.map((v) => imageWithThumbnail(v.file)));
    imageGroups.push(done.map((d) => d.image));
    thumbnailGroups.push(done.map((d) => d.thumbnail));
  }
  return { imageGroups, thumbnailGroups };
}
